/**
 * Branding service — loads and applies branding assets (logos, favicon, etc.)
 * Fetches from /api/branding (public, no auth) and caches the result.
 */

import { store } from '../state/store.js';
import { t } from '../i18n/index.js';

let brandingCache = null;
let loadPromise = null;

/**
 * Fetch branding assets. Returns { 'logo-primary': '/uploads/branding/...', ... }
 * Also stores in the global store so navigation.js can use it on render.
 */
export async function loadBranding() {
  if (brandingCache) return brandingCache;
  if (loadPromise) return loadPromise;

  // i18n-fetch-ok: public config GET, no user-visible server text
  loadPromise = fetch('/api/branding')
    .then(r => r.ok ? r.json() : {})
    .catch(() => ({}));

  brandingCache = await loadPromise;
  loadPromise = null;

  // Normalise keys (API returns 'logo-white', store needs both forms)
  try {
    const b = {};
    for (const [k, v] of Object.entries(brandingCache)) {
      b[k] = v;
      b[k.replace(/-/g, '_')] = v;  // logo-white -> logo_white
    }
    store.set('branding', b);
  } catch { /* ignore */ }

  return brandingCache;
}

/**
 * Get a branding asset URL by slot name.
 * Returns null if not uploaded.
 */
export function getBrandingUrl(slot) {
  if (!brandingCache) return null;
  return brandingCache[slot] || null;
}

/**
 * Apply branding to the current page.
 * Call this after the DOM is ready.
 */
export async function applyBranding() {
  const branding = await loadBranding();

  // Sidebar logo
  const sidebarLogoImg = document.querySelector('.sidebar-logo-img');
  if (sidebarLogoImg) {
    const logoUrl = branding['logo-white'] || branding['logo-icon'] || branding['logo-primary'];
    if (logoUrl) {
      sidebarLogoImg.src = logoUrl;
      sidebarLogoImg.style.display = '';
      const fallback = document.querySelector('.sidebar-logo-fallback');
      if (fallback) fallback.style.display = 'none';
    }
  } else {
    const sidebarIcon = document.querySelector('.sidebar-logo-icon');
    if (sidebarIcon) {
      const logoUrl = branding['logo-white'] || branding['logo-icon'] || branding['logo-primary'];
      if (logoUrl) {
        sidebarIcon.innerHTML = `<img src="${logoUrl}" alt="${t('nav.logo')}" style="width:100%;height:100%;object-fit:contain;">`;
        sidebarIcon.style.background = 'transparent';
        sidebarIcon.style.padding = '2px';
      }
    }
  }

  // Public form logo
  const publicLogo = document.querySelector('.public-logo');
  if (publicLogo) {
    const logoUrl = branding['logo-primary'] || branding['logo-icon'];
    if (logoUrl) {
      publicLogo.innerHTML = `<img src="${logoUrl}" alt="${t('nav.logo')}" style="width:100%;height:100%;object-fit:contain;">`;
      publicLogo.style.background = 'transparent';
      publicLogo.style.width = '180px';
      publicLogo.style.height = 'auto';
      publicLogo.style.padding = '0';
      publicLogo.style.borderRadius = '0';
    }
  }

  // Login page logos
  const loginHeroLogo = document.querySelector('.login-hero-logo');
  if (loginHeroLogo) {
    const logoUrl = branding['logo-white'] || branding['logo-primary'];
    if (logoUrl) {
      loginHeroLogo.innerHTML = `<img src="${logoUrl}" alt="${t('nav.logo')}" style="max-width:200px;height:auto;">`;
      loginHeroLogo.style.background = 'transparent';
      loginHeroLogo.style.width = 'auto';
      loginHeroLogo.style.height = 'auto';
      loginHeroLogo.style.fontSize = '0';
      loginHeroLogo.style.padding = '0';
    }
  }
  const loginMobileLogo = document.querySelector('.login-mobile-logo .sidebar-logo-icon');
  if (loginMobileLogo) {
    const logoUrl = branding['logo-primary'] || branding['logo-icon'];
    if (logoUrl) {
      loginMobileLogo.innerHTML = `<img src="${logoUrl}" alt="${t('nav.logo')}" style="width:100%;height:100%;object-fit:contain;">`;
      loginMobileLogo.style.background = 'transparent';
      loginMobileLogo.style.padding = '2px';
    }
  }

  // Login background
  const loginHero = document.querySelector('.login-hero');
  if (loginHero && branding['login-background']) {
    loginHero.style.backgroundImage = `linear-gradient(135deg, rgba(0,34,51,0.92), rgba(0,51,77,0.88)), url(${branding['login-background']})`;
    loginHero.style.backgroundSize = 'cover';
    loginHero.style.backgroundPosition = 'center';
  }

  // Favicon
  if (branding['favicon']) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding['favicon'];
  }
}

/**
 * Clear the cache (e.g. after admin uploads new branding).
 */
export function clearBrandingCache() {
  brandingCache = null;
  loadPromise = null;
}
